import type {
  ModelFallbackInfo,
  ContextCompactedInfo,
  ModelOption,
  OrganizationModelPolicy
} from '@axilon-apps/domains-ontology-ui-state'

const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  bedrock: 'Bedrock'
}

const EXTRA_MODEL_LABELS: Record<string, string> = {
  'gpt-5.1': 'GPT-5.1',
  'gpt-5.5': 'GPT-5.5',
  'claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'grok-4.3': 'Grok 4.3',
  'gpt-5.6-sol': 'Sol',
  'gpt-5.6-terra': 'Terra',
  'gpt-5.6-luna': 'Luna',
  'gpt-6-astra': 'GPT-6 Astra',
  'gpt-6.1-sol': 'GPT-6.1 Sol',
  'gpt-6-sol': 'GPT-6 Sol',
  'gpt-6-luna': 'GPT-6 Luna',
  'claude-sonnet-5-5': 'Claude Sonnet 5.5',
  'claude-haiku-5-5': 'Claude Haiku 5.5'
}

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider
}

/** Resolve a provider/model pair to its catalog display label. */
export function modelLabel(
  policy: OrganizationModelPolicy | undefined,
  provider: string,
  model: string
): string {
  const catalog = policy?.catalog.find(
    (option) => option.provider === provider && option.model === model
  )?.label
  return catalog ?? EXTRA_MODEL_LABELS[model] ?? model
}

export function modelTargetKey(provider: string, model: string): string {
  return `${provider}/${model}`
}

export function isModelAllowed(
  policy: OrganizationModelPolicy,
  provider: string,
  model: string
): boolean {
  const key = modelTargetKey(provider, model)
  return policy.allowedModels.some(
    (target) => modelTargetKey(target.provider, target.model) === key
  )
}

export function allowedModelOptions(policy: OrganizationModelPolicy): ModelOption[] {
  return policy.catalog.filter((option) => isModelAllowed(policy, option.provider, option.model))
}

/**
 * Chat marker for a turn whose earlier context was summarized. Says who
 * compacted and, when known, how full the window was.
 */
export function formatContextCompactedNotice(
  info: ContextCompactedInfo,
  policy?: OrganizationModelPolicy
): string {
  const who = info.mode === 'emulated' ? 'Axilon' : providerLabel(info.provider)
  const model = info.model ? ` for ${modelLabel(policy, info.provider, info.model)}` : ''
  const why =
    info.reason === 'overflow_retry' ? ' after the request exceeded the context window' : ''
  const fill =
    info.inputTokens !== undefined &&
    info.contextWindowTokens !== undefined &&
    info.contextWindowTokens > 0
      ? ` (${Math.round((info.inputTokens / info.contextWindowTokens) * 100)}% of the window was in use)`
      : ''
  return `Earlier conversation summarized by ${who}${model}${why}${fill}`
}

export function formatModelFallbackNotice(
  info: ModelFallbackInfo,
  policy?: OrganizationModelPolicy
): string {
  const from = `${modelLabel(policy, info.requestedProvider, info.requestedModel)} (${providerLabel(info.requestedProvider)})`
  const to = `${modelLabel(policy, info.provider, info.model)} (${providerLabel(info.provider)})`
  return `Used ${to} instead of ${from}`
}
